import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiBox, FiPlus, FiSearch, FiTrash2, FiUser, FiArrowRight, FiX, FiEdit2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import ConfirmModal from '../shared/ConfirmModal';
import { normalizeRole } from '../../finance/auth/permissions';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export default function ProductManagement() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, loading: false });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ productname: '', description: '', status: 'ACTIVE', budget: '', clientName: '' });

  const role = localStorage.getItem('role') || '';
  const normalizedRole = normalizeRole(role);
  const isAdmin = normalizedRole === 'ADMIN' || normalizedRole === 'SUPERADMIN' || normalizedRole === 'TESTER';

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/product/getAllProducts');
      setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []));
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.productname) {
        toast.error('Product name is required');
        return;
      }
      
      const payload = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : 0,
        clientName: formData.clientName ? { name: formData.clientName } : null
      };

      if (isEditing) {
        await api.put(`/product/${editingId}`, payload);
        toast.success('Product updated successfully');
      } else {
        await api.post('/product/createProduct', payload);
        toast.success('Product created successfully');
      }
      
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} product`);
    }
  };

  const openEditModal = (product) => {
    setEditingId(product.id);
    setIsEditing(true);
    setFormData({
      productname: product.productname || '',
      description: product.description || '',
      status: product.status || 'ACTIVE',
      budget: product.budget || '',
      clientName: (() => {
        const c = product.clientName;
        if (!c) return '';
        if (Array.isArray(c)) {
          const first = c[0];
          if (typeof first === 'object') return first.name || first.client1 || '';
          return first;
        }
        if (typeof c === 'object') return c.name || c.client1 || '';
        return c;
      })()
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ open: true, id, loading: false });
  };

  const confirmDelete = async () => {
    setDeleteConfirm(prev => ({ ...prev, loading: true }));
    try {
      await api.delete(`/product/deleteProduct/${deleteConfirm.id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (err) {
      toast.error('Failed to delete product');
    } finally {
      setDeleteConfirm({ open: false, id: null, loading: false });
    }
  };

  const filteredProducts = products.filter(p => 
    p.productname?.toLowerCase().includes(search.toLowerCase()) ||
    p.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
    <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, loading: false })}
        onConfirm={confirmDelete}
        loading={deleteConfirm.loading}
        title="Delete Product?"
        message="Are you sure you want to delete this product?"
        confirmText="Yes, Delete"
      />
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FiBox className="text-blue-600" /> Products
          </h2>
          <p className="text-slate-500 text-sm mt-1">Manage software products and track their development.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search products..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-2xs"
            />
          </div>
          {isAdmin && (
            <button 
              onClick={() => { 
                setIsEditing(false);
                setEditingId(null);
                setFormData({ productname: '', description: '', status: 'ACTIVE', budget: '', clientName: '' }); 
                setShowModal(true); 
              }}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-medium transition-colors shadow-sm cursor-pointer shrink-0"
            >
              <FiPlus /> New Product
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
        {loading ? (
          <div className="col-span-full py-12 text-center text-slate-500">Loading products...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="col-span-full py-16 text-center bg-white rounded-2xl border border-slate-200">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiBox className="text-2xl text-slate-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">No products found</h3>
            <p className="text-slate-500 text-sm mb-4">Get started by creating your first product.</p>
            {isAdmin && (
              <button 
                onClick={() => { 
                  setIsEditing(false);
                  setEditingId(null);
                  setFormData({ productname: '', description: '', status: 'ACTIVE', budget: '', clientName: '' }); 
                  setShowModal(true); 
                }}
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <FiPlus /> Create Product
              </button>
            )}
          </div>
        ) : (
          filteredProducts.map(product => (
            <div key={product.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all overflow-hidden flex flex-col">
              <div className="p-5 flex-1">
                <div className="flex justify-between items-start mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg">
                    {product.productname?.charAt(0).toUpperCase()}
                  </div>
                  <span className={`px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wide ${
                    product.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {product.status || 'UNKNOWN'}
                  </span>
                </div>
                
                <h3 className="text-[15px] font-bold text-slate-800 mb-1.5 truncate">{product.productname}</h3>
                <p className="text-slate-500 text-[13px] mb-3 line-clamp-2 min-h-[36px]">
                  {product.description || 'No description provided.'}
                </p>

                <div className="flex items-center gap-3 text-[13px] text-slate-600 mb-1">
                  <div className="flex items-center gap-1.5">
                    <FiUser className="text-slate-400" />
                    <span className="truncate max-w-[120px]">
                      {(() => {
                        const c = product.clientName;
                        if (!c) return 'Internal';
                        if (Array.isArray(c)) {
                          const first = c[0];
                          if (typeof first === 'object') return first.name || first.client1 || 'Internal';
                          return first;
                        }
                        if (typeof c === 'object') return c.name || c.client1 || 'Internal';
                        return c;
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-slate-700">${product.budget ? product.budget.toLocaleString() : '0'}</span>
                  </div>
                </div>
              </div>
              
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between mt-auto">
                <button 
                  onClick={() => {
                    sessionStorage.setItem(`product_${product.id}`, JSON.stringify(product));
                    navigate(`/EmpProducts/${product.id}`);
                  }}
                  className="text-[13px] font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                >
                  View Details <FiArrowRight size={14} />
                </button>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => openEditModal(product)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button 
                      onClick={() => handleDelete(product.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex justify-end p-4 sm:p-6">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl flex flex-col h-full overflow-hidden transform transition-transform duration-300 translate-x-0">
            <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <h3 className="text-lg font-bold text-slate-800">{isEditing ? 'Edit Product' : 'Create New Product'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 bg-white shadow-sm border border-slate-200 rounded-full p-1.5"><FiX size={16} /></button>
            </div>
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Product Name *</label>
                <input 
                  type="text" 
                  value={formData.productname}
                  onChange={(e) => setFormData({...formData, productname: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  placeholder="e.g. ERP Core"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
                <textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  placeholder="What does this product do?"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Budget</label>
                  <input 
                    type="number" 
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                  <select 
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="PLANNING">Planning</option>
                    <option value="ON_HOLD">On Hold</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Client / Customer</label>
                <input 
                  type="text" 
                  value={formData.clientName}
                  onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  placeholder="e.g. Internal"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 shrink-0">
              <button 
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-200 bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreate}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
              >
                {isEditing ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
